import { Fragment } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
} from '@material-ui/core'
import { LoadingButton } from '@material-ui/lab'
import {
  Add,
  Delete,
  DragIndicator,
  LocationOn,
  NoteAdd,
  Remove,
  UploadFile,
} from '@material-ui/icons'
import { Icon } from '@iconify/react'
import edit2Fill from '@iconify/icons-eva/edit-2-fill'
import checkmarkFill from '@iconify/icons-eva/checkmark-fill'
import closeFill from '@iconify/icons-eva/close-fill'
import { experimentalStyled as styled } from '@material-ui/core/styles'
import { Draggable } from 'react-beautiful-dnd'
import Modal from '../../../components/_dashboard/blog/ProjectModel'
import { fUSPrice } from 'src/utils/formatNumber'
import {
  getCatalogSourceCreateOptionLabel,
  getCatalogSourceOptionLabel,
  getGroupAnchorIndexId,
  getLineProductLabel,
  isIndexIdInList,
  isQuotationChooseCatalogRow,
  isQuotationWriteRow,
  getNestedLineMarketingAdjustmentValue,
  formatNestedMarketingAdjustmentDisplay,
  getNestedLineMarketingAdjValueType,
  getNestedLineLosingMoneyFlags,
  getLosingMoneyRowSx,
  getSingleProductFormulaCostEach,
  getValidUngroupedSelectionIds,
  restoreUngroupedProductMarketing,
  snapshotGroupMemberMarketing,
  withInheritedGroupNotes,
  QUOTATION_ROW_DELETE_CELL_SX,
  QUOTATION_ROW_ACTIONS_BOX_SX,
  QUOTATION_ROW_ACTIONS_LEADING_SLOT_SX,
} from './quotationGroupingHelpers'
import { clearCatalogInstallCost } from 'src/utils/retailPriceEdit'
import {
  getDesignOrderLosingMoneyRowSx,
  DESIGN_ORDER_ROW_ACTIONS_BOX_SX,
  DESIGN_ORDER_ROW_ACTIONS_CELL_SX,
  DESIGN_ORDER_ROW_ACTIONS_LEADING_SLOT_SX,
} from '../designOrder/designOrderProductRowHelpers'
import quickbookLogo from 'src/assets/quickbookLogo-removebg-preview.png'

const hasQuickbooksSync = (product) =>
  Number(product?.syncWithQuickbooks) === 1 || Number(product?.syncWithQuickBook) === 1

const groupHasAnyQuickbooksSync = (products = []) => (products || []).some(hasQuickbooksSync)

function QuotationQuickbooksCell({ product, products }) {
  const showIcon =
    product != null ? hasQuickbooksSync(product) : groupHasAnyQuickbooksSync(products)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 20,
      }}
    >
      {showIcon ? (
        <img
          src={quickbookLogo}
          alt="QuickBooks"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : null}
    </div>
  )
}

const LabelStyle = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtitle2,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}))

export const getGroupRowAlignSx = () => ({ '& .MuiTableCell-root': { verticalAlign: 'middle' } })

export const getGroupIconCellSx = () => ({ verticalAlign: 'middle', textAlign: 'center', px: 0.5 })

/** Retail columns (id 5/6): quotation renders both cells; design order uses one active column only. */
const shouldRenderValueColumn = (val, isDesignTable = false, markupType = 2) => {
  if (isDesignTable && val.id === 5) return true
  if (isDesignTable && val.id === 6) return false
  if (val.value !== null && val.value !== '') return true
  if (!isDesignTable && (val.id === 5 || val.id === 6)) return true
  if (isDesignTable && Number(markupType ?? 2) === 2 && val.id === 5) return true
  if (isDesignTable && Number(markupType ?? 2) === 1 && val.id === 6) return true
  if (val.id === 13) return true
  return false
}

export const GroupCellIcon = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
    {children}
  </Box>
)

export const GroupDeleteIconButton = ({ isDesignTable, onClick }) => (
  <IconButton
    color="error"
    {...(isDesignTable ? {} : { size: 'small' })}
    onClick={onClick}
  >
    <Delete {...(isDesignTable ? {} : { fontSize: 'small' })} />
  </IconButton>
)

export const GroupEditActionButtons = ({
  isDesignTable,
  isEditing,
  onEdit,
  onUpdate,
  onCancel,
}) => {
  if (isEditing) {
    return (
      <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="center">
        <IconButton size="small" onClick={onUpdate} aria-label="Update group">
          <Icon icon={checkmarkFill} width={20} height={20} color="#22c55e" />
        </IconButton>
        <IconButton size="small" onClick={onCancel} aria-label="Cancel edit">
          <Icon icon={closeFill} width={20} height={20} color="#ef4444" />
        </IconButton>
      </Stack>
    )
  }

  return (
    <IconButton
      size="small"
      color={isDesignTable ? 'primary' : 'default'}
      onClick={onEdit}
      aria-label="Edit group"
    >
      <Icon icon={edit2Fill} width={20} height={20} color={isDesignTable ? undefined : '#00b8d9'} />
    </IconButton>
  )
}

export function QuotationGroupDialogs({
  openAddToGroupModal,
  setOpenAddToGroupModal,
  addToGroupOptions,
  addToGroupId,
  setAddToGroupId,
  handleAddToExistingGroup,
  groupSelectedProductIds,
  openGroupCatalogSourceModal,
  resetGroupCatalogSourceModal,
  showGroupCatalogCreateForm,
  groupCatalogCustomName,
  setGroupCatalogCustomName,
  groupCatalogCustomDescription,
  setGroupCatalogCustomDescription,
  groupCatalogCreateError,
  setGroupCatalogCreateError,
  getCatalogSourceDropdownOptions,
  groupCatalogSourceInputValue,
  setGroupCatalogSourceInputValue,
  setGroupCatalogSourceIndexId,
  setGroupCatalogCustomSourceId,
  setShowGroupCatalogCreateForm,
  groupCatalogCustomSourceId,
  customCatalogGroupSources,
  groupCatalogSourceIndexId,
  getSelectedLineCatalogProducts,
  handleCancelGroupCatalogCreate,
  handleCreateCustomCatalogGroup,
  buildGroupFromSelection,
}) {
  return (
    <>
      <Dialog
        open={openAddToGroupModal}
        onClose={() => setOpenAddToGroupModal(false)}
        fullWidth
        maxWidth="sm"
        disableScrollLock
      >
        <DialogTitle>Add Products to Existing Group</DialogTitle>
        <DialogContent>
          <Autocomplete
            fullWidth
            sx={{ mt: 1 }}
            options={addToGroupOptions}
            getOptionLabel={(option) =>
              option?.group_name ?? option?.name ?? option?.groupName ?? (option?.id != null ? `Group ${option.id}` : '')
            }
            value={
              addToGroupOptions?.find((g) => String(g?.id ?? g?.group_id) === String(addToGroupId)) ||
              null
            }
            onChange={(_, newValue) => {
              setAddToGroupId(newValue ? (newValue.id ?? newValue.group_id) : '')
            }}
            renderInput={(params) => <TextField {...params} label="Select Group" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddToGroupModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddToExistingGroup}
            disabled={!addToGroupId || groupSelectedProductIds.length === 0}
          >
            Add to Group
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openGroupCatalogSourceModal}
        onClose={resetGroupCatalogSourceModal}
        fullWidth
        maxWidth="sm"
        disableScrollLock
      >
        <DialogTitle>Choose Catalog Source</DialogTitle>
        <DialogContent>
          {showGroupCatalogCreateForm ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                required
                label="Name"
                value={groupCatalogCustomName}
                onChange={(event) => {
                  setGroupCatalogCustomName(event.target.value)
                  if (groupCatalogCreateError) setGroupCatalogCreateError('')
                }}
                error={Boolean(groupCatalogCreateError)}
              />
              <TextField
                fullWidth
                label="Description"
                value={groupCatalogCustomDescription}
                onChange={(event) => setGroupCatalogCustomDescription(event.target.value)}
                multiline
                minRows={2}
              />
              {groupCatalogCreateError ? (
                <FormHelperText error>{groupCatalogCreateError}</FormHelperText>
              ) : null}
            </Stack>
          ) : (
            <Autocomplete
              fullWidth
              sx={{ mt: 1 }}
              openOnFocus
              clearOnBlur={false}
              options={getCatalogSourceDropdownOptions(groupCatalogSourceInputValue)}
              filterOptions={(options) => options}
              inputValue={groupCatalogSourceInputValue}
              onInputChange={(_, value, reason) => {
                setGroupCatalogSourceInputValue(value)
                if (reason === 'input') {
                  setGroupCatalogSourceIndexId(null)
                  setGroupCatalogCustomSourceId(null)
                }
              }}
              getOptionLabel={getCatalogSourceOptionLabel}
              value={(() => {
                if (groupCatalogCustomSourceId) {
                  return (
                    customCatalogGroupSources.find(
                      (source) => String(source?.id) === String(groupCatalogCustomSourceId),
                    ) || null
                  )
                }
                return (
                  getSelectedLineCatalogProducts(groupSelectedProductIds).find(
                    (item) => String(item?.indexId) === String(groupCatalogSourceIndexId),
                  ) || null
                )
              })()}
              onChange={(_, newValue) => {
                if (newValue?.isCreateNew) {
                  setGroupCatalogCustomName((newValue.name || '').trim())
                  setGroupCatalogCustomDescription('')
                  setGroupCatalogCreateError('')
                  setShowGroupCatalogCreateForm(true)
                  setGroupCatalogSourceIndexId(null)
                  setGroupCatalogCustomSourceId(null)
                  return
                }
                if (newValue?.isCustomGroupSource) {
                  setGroupCatalogCustomSourceId(newValue.id)
                  setGroupCatalogSourceIndexId(null)
                  setGroupCatalogSourceInputValue((newValue.name || '').trim())
                  return
                }
                setGroupCatalogSourceIndexId(newValue?.indexId ?? null)
                setGroupCatalogCustomSourceId(null)
                setGroupCatalogSourceInputValue(newValue ? getLineProductLabel(newValue) : '')
              }}
              isOptionEqualToValue={(option, value) => {
                if (option?.isCreateNew || value?.isCreateNew) return false
                if (option?.isCustomGroupSource || value?.isCustomGroupSource) {
                  return String(option?.id) === String(value?.id)
                }
                return String(option?.indexId) === String(value?.indexId)
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  {option.isCreateNew ? (
                    <Typography color="primary" sx={{ fontWeight: 500 }}>
                      {getCatalogSourceCreateOptionLabel(option.name)}
                    </Typography>
                  ) : (
                    getCatalogSourceOptionLabel(option)
                  )}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Catalog item for group name/description" />
              )}
            />
          )}
        </DialogContent>
        <DialogActions>
          {showGroupCatalogCreateForm ? (
            <>
              <Button onClick={handleCancelGroupCatalogCreate}>Cancel</Button>
              <Button variant="contained" onClick={handleCreateCustomCatalogGroup}>
                Create
              </Button>
            </>
          ) : (
            <>
              <Button onClick={resetGroupCatalogSourceModal}>Cancel</Button>
              <Button
                variant="contained"
                onClick={() => {
                  const lineCatalogProducts = getSelectedLineCatalogProducts(groupSelectedProductIds)
                  const anchorProduct = lineCatalogProducts[0]
                  if (groupCatalogCustomSourceId) {
                    const customSource = customCatalogGroupSources.find(
                      (source) => String(source?.id) === String(groupCatalogCustomSourceId),
                    )
                    buildGroupFromSelection(anchorProduct, {
                      name: customSource?.name,
                      description: customSource?.description || '',
                    })
                    return
                  }
                  const chosenProduct = lineCatalogProducts.find(
                    (item) => String(item?.indexId) === String(groupCatalogSourceIndexId),
                  )
                  // If no catalog option is chosen, buildGroupFromSelection will fall back to a selected member.
                  buildGroupFromSelection(chosenProduct)
                }}
              >
                Continue
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  
  )
}

export function QuotationGroupActionBar({
  groupSelectedProductIds,
  groupEditMode,
  selectedProducts,
  handleCreateGroupFromSelection,
  setOpenAddToGroupModal,
}) {
  const isAnyGroupInEditMode = Object.values(groupEditMode || {}).some(Boolean)
  const validSelectedIds = getValidUngroupedSelectionIds(
    selectedProducts,
    groupSelectedProductIds,
  )
  const hasExistingGroups = (selectedProducts || []).some((p) => p.group_id)
  if (validSelectedIds.length === 0 || isAnyGroupInEditMode) return null
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
      {validSelectedIds.length > 1 && (
        <Button variant="outlined" size="small" onClick={handleCreateGroupFromSelection}>
          Group Selected Products
        </Button>
      )}
      {hasExistingGroups && (
        <Button variant="outlined" size="small" onClick={() => setOpenAddToGroupModal(true)}>
          Add to Existing Group
        </Button>
      )}
    </Box>
  )
}

export function QuotationGroupExpandedTable({
  group,
  isGroupOpen,
  groupInstallLosingMoneyRowSx,
  VALUE,
  markupType,
  hasMarkupPermission,
  theme,
  groupProductQuantityDrafts,
  groupEditMode,
  groupSelectedProductIds,
  toggleGroupProductSelection,
  handleOpenNote,
  handleOpenAttachment,
  handleOpenProductFormula,
  handleOpenInstallNote,
  handleOpenGroupInstallNote,
  getProductAttachmentsList,
  handleGroupLineQuantityDraftChange,
  handleGroupInstallQuantityChange,
  handleGroupInstallMarketingTypeChange,
  handleGroupInstallMarketingAdjustmentChange,
  handleGroupAllLocationSameChange,
  handleGroupSingleCardChange,
  handleGroupLocationFieldChange,
  groupLocationStates,
  setGroupLocationStates,
  setSelectedProducts,
  setRefreshCalculation,
  handleDeleteGroupInstall,
  renderProductAttachmentModal,
  isUploadingProduct,
  getProductAttachmentRowProps,
  projectCategoryId,
  tableVariant = 'quotation',
  displaySlNo,
  productColSx,
  descriptionColSx,
}) {
  const isDesignTable = tableVariant === 'design'
  const effectiveMarkupType = isDesignTable ? Number(markupType ?? 2) : markupType
  const groupAnchorIndexId = getGroupAnchorIndexId(group)
  const groupAnchorProduct = (group?.products || []).find(
    (p) => Number(p?.indexId) === Number(groupAnchorIndexId),
  )
  const groupHasAttachments =
    groupAnchorIndexId != null &&
    typeof getProductAttachmentsList === 'function' &&
    getProductAttachmentsList(groupAnchorIndexId).length > 0
  const groupInstallNotes =
    (group?.products || []).find(
      (p) => p?.install_notes != null && String(p.install_notes).trim() !== '',
    )?.install_notes ||
    groupAnchorProduct?.install_notes ||
    ''
  const groupHasInstallNotes = Boolean(groupInstallNotes && String(groupInstallNotes).trim())
  const resolveLosingMoneyRowSx = (isLosingMoney) =>
    isDesignTable
      ? getDesignOrderLosingMoneyRowSx(theme, isLosingMoney)
      : getLosingMoneyRowSx(theme, isLosingMoney)

  return (
    <>
      {Number(group.totalInstallSubtotal || 0) > 0 ? (
        <TableRow sx={{ ...groupInstallLosingMoneyRowSx, ...getGroupRowAlignSx() }}>
          <TableCell sx={getGroupIconCellSx()} />
          <TableCell sx={getGroupIconCellSx()} />
          {VALUE?.map((val) => {
            if (!shouldRenderValueColumn(val, isDesignTable, markupType)) {
              return null
            }
            if (val.id === 1) {
              return (
                <TableCell key={`gi-lead-${val.id}`} align={isDesignTable ? 'center' : undefined}>
                  {isDesignTable ? `#${displaySlNo}` : null}
                </TableCell>
              )
            }
            if (val.id === 24) {
              return (
                <TableCell key={`gi-lead-${val.id}`} align={isDesignTable ? 'center' : undefined}>
                  {isDesignTable ? '-' : null}
                </TableCell>
              )
            }
            if (val.id === 22 && !isDesignTable) {
              return <TableCell key={`gi-lead-${val.id}`} />
            }
            if (val.id === 2) {
              return (
                <TableCell
                  key={`gi-name-${val.id}`}
                  align={isDesignTable ? 'center' : undefined}
                  sx={isDesignTable ? productColSx : { fontWeight: 600 }}
                >
                  <Typography
                    sx={{
                      ...(isDesignTable ? { textAlign: 'center' } : {}),
                      ...(groupHasInstallNotes
                        ? {
                            color: 'red',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }
                        : {}),
                    }}
                  >
                    {groupHasInstallNotes ? groupInstallNotes : 'Install Price'}
                  </Typography>
                </TableCell>
              )
            }
            if (val.id === 25) {
              if (isDesignTable) {
                return (
                  <TableCell key={`gi-desc-${val.id}`} align="center" sx={descriptionColSx}>
                    -
                  </TableCell>
                )
              }
              return <TableCell key={`gi-empty-${val.id}`} />
            }
            if (val.id === 19) {
              if (isDesignTable) {
                return (
                  <TableCell key={`gi-att-${val.id}`} align="center">
                    -
                  </TableCell>
                )
              }
              return <TableCell key={`gi-empty-${val.id}`} />
            }
            if (val.id === 7) {
              return <TableCell key={`gi-empty-${val.id}`} />
            }
            if (val.id === 3) {
              return (
                <TableCell
                  key={`gi-note-${val.id}`}
                  align="center"
                  sx={getGroupIconCellSx()}
                >
                  <GroupCellIcon>
                    {groupAnchorIndexId != null ? (
                      <IconButton
                        size={isDesignTable ? 'small' : 'medium'}
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          const note = groupInstallNotes || groupAnchorProduct?.install_notes || ''
                          if (typeof handleOpenGroupInstallNote === 'function') {
                            handleOpenGroupInstallNote(groupAnchorIndexId, note)
                            return
                          }
                          if (typeof handleOpenInstallNote === 'function') {
                            handleOpenInstallNote(groupAnchorIndexId, note)
                          }
                        }}
                      >
                        <NoteAdd
                          sx={{
                            color: groupHasInstallNotes
                              ? theme.palette.primary.main
                              : '',
                          }}
                        />
                      </IconButton>
                    ) : null}
                  </GroupCellIcon>
                </TableCell>
              )
            }
            if (val.id === 4) {
              return (
                <TableCell
                  key={`gi-formula-${val.id}`}
                  align={isDesignTable ? 'center' : undefined}
                  sx={!isDesignTable ? { fontWeight: 600 } : undefined}
                >
                  {isDesignTable
                    ? `$${fUSPrice(Number(group.baseInstallFormulaEach || 0))}`
                    : Number(group.baseInstallFormulaEach || 0).toFixed(2)}
                </TableCell>
              )
            }
            if (val.id === 20) {
              return (
                <TableCell
                  key={`gi-markup-${val.id}`}
                  align="center"
                  sx={!isDesignTable ? { fontWeight: 600 } : undefined}
                >
                  -
                </TableCell>
              )
            }
            if (val.id === 5 && (isDesignTable || effectiveMarkupType != 1)) {
              return (
                <TableCell
                  key={`gi-retail5-${val.id}`}
                  align={isDesignTable ? 'center' : undefined}
                  sx={!isDesignTable ? { fontWeight: 600 } : undefined}
                >
                  {isDesignTable ? (
                    <>
                      {hasMarkupPermission
                        ? `$${fUSPrice(Number(group.adjustedGroupInstallRetailEach || 0).toFixed(2))}`
                        : '-'}
                      {/* Future: read-only TextField for design group install retail
                      {hasMarkupPermission ? (
                        <TextField
                          size="small"
                          label="Install Price Each"
                          sx={{ width: 120 }}
                          value={Number(group.adjustedGroupInstallRetailEach || 0).toFixed(2)}
                          InputProps={{ readOnly: true }}
                        />
                      ) : (
                        '-'
                      )}
                      */}
                    </>
                  ) : hasMarkupPermission && effectiveMarkupType == 2 ? (
                    Number(group.adjustedGroupInstallRetailEach || 0).toFixed(2)
                  ) : null}
                </TableCell>
              )
            }
            if (val.id === 6 && !isDesignTable && effectiveMarkupType != 2) {
              return (
                <TableCell
                  key={`gi-retail6-${val.id}`}
                  align={isDesignTable ? 'center' : undefined}
                  sx={!isDesignTable ? { fontWeight: 600 } : undefined}
                >
                  {isDesignTable ? (
                    <>
                      {hasMarkupPermission
                        ? `$${fUSPrice(Number(group.adjustedGroupInstallRetailEach || 0).toFixed(2))}`
                        : '-'}
                      {/* Future: read-only TextField for design group install retail
                      {hasMarkupPermission ? (
                        <TextField
                          size="small"
                          label="Install Price Each"
                          sx={{ width: 120 }}
                          value={Number(group.adjustedGroupInstallRetailEach || 0).toFixed(2)}
                          InputProps={{ readOnly: true }}
                        />
                      ) : (
                        '-'
                      )}
                      */}
                    </>
                  ) : hasMarkupPermission && effectiveMarkupType == 1 ? (
                    Number(group.adjustedGroupInstallRetailEach || 0).toFixed(2)
                  ) : null}
                </TableCell>
              )
            }
            if (val.id === 9) {
              return (
                <TableCell
                  key={`gi-qty-${val.id}`}
                  align={isDesignTable ? 'center' : undefined}
                  sx={!isDesignTable ? { fontWeight: 600, width: 110 } : undefined}
                >
                  <TextField
                    type="number"
                    {...(isDesignTable
                      ? { size: 'small', style: { width: 72 } }
                      : { fullWidth: true, label: 'Qty' })}
                    value={Number(group.groupInstallQuantity || 1)}
                    onKeyPress={(event) => {
                      if (
                        (event.key === '0' && event.target.value === '') ||
                        event.key === '-' ||
                        event.key === '.'
                      ) {
                        event.preventDefault()
                      }
                    }}
                    onChange={(event) =>
                      handleGroupInstallQuantityChange(group.id, event.target.value)
                    }
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </TableCell>
              )
            }
            if (val.id === 10) {
              return (
                <TableCell
                  key={`gi-loc-${val.id}`}
                  align="center"
                  sx={
                    isDesignTable
                      ? getGroupIconCellSx()
                      : { fontWeight: 600, color: 'text.secondary' }
                  }
                >
                  -
                </TableCell>
              )
            }
            if (val.id === 11) {
              return (
                <TableCell
                  key={`gi-adjtype-${val.id}`}
                  align={isDesignTable ? 'center' : undefined}
                  sx={!isDesignTable ? { color: 'text.secondary', width: 150 } : undefined}
                >
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                    <Remove
                      fontSize="small"
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        handleGroupInstallMarketingTypeChange(
                          group.id,
                          false,
                          group.totalInstallFormulaEach,
                          group.totalInstallRetailEach,
                          group.groupInstallAdjustment,
                        )
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            group.groupInstallAdjValueType !== false &&
                            group.groupInstallAdjValueType !== 0
                          }
                          onChange={(event) =>
                            handleGroupInstallMarketingTypeChange(
                              group.id,
                              event.target.checked,
                              group.totalInstallFormulaEach,
                              group.totalInstallRetailEach,
                              group.groupInstallAdjustment,
                            )
                          }
                        />
                      }
                      label=""
                    />
                    <Add
                      fontSize="small"
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        handleGroupInstallMarketingTypeChange(
                          group.id,
                          true,
                          group.totalInstallFormulaEach,
                          group.totalInstallRetailEach,
                          group.groupInstallAdjustment,
                        )
                      }
                    />
                  </Stack>
                </TableCell>
              )
            }
            if (val.id === 15) {
              return (
                <TableCell
                  key={`gi-adj-${val.id}`}
                  align={isDesignTable ? 'center' : undefined}
                  sx={!isDesignTable ? { fontWeight: 600, width: 120 } : undefined}
                >
                  <TextField
                    type="number"
                    {...(isDesignTable ? { size: 'small', style: { width: 90 } } : { fullWidth: true })}
                    inputProps={{ min: 0 }}
                    label="$"
                    value={Number(group.groupInstallAdjustment) || ''}
                    onWheel={(e) => e.target.blur()}
                    onChange={(event) =>
                      handleGroupInstallMarketingAdjustmentChange(
                        group.id,
                        event.target.value,
                        group.totalInstallFormulaEach,
                        group.totalInstallRetailEach,
                        group.groupInstallAdjValueType,
                      )
                    }
                  />
                </TableCell>
              )
            }
            if (val.id === 12) {
              return (
                <TableCell
                  key={`gi-sub-${val.id}`}
                  align={isDesignTable ? 'center' : undefined}
                  sx={!isDesignTable ? { fontWeight: 600 } : undefined}
                >
                  {isDesignTable
                    ? `$${fUSPrice(Number(group.adjustedGroupInstallSubtotal || 0).toFixed(2))}`
                    : Number(group.adjustedGroupInstallSubtotal || 0).toFixed(2)}
                </TableCell>
              )
            }
            if (val.id === 14) {
              return (
                <TableCell
                  key={`gi-total-${val.id}`}
                  align={isDesignTable ? 'center' : undefined}
                  sx={!isDesignTable ? { fontWeight: 600 } : undefined}
                >
                  {isDesignTable
                    ? `$${fUSPrice(Number(group.adjustedGroupTotal || 0).toFixed(2))}`
                    : Number(group.adjustedGroupTotal || 0).toFixed(2)}
                </TableCell>
              )
            }
            if (val.id === 13) {
              return (
                <TableCell
                  key={`gi-del-${val.id}`}
                  align="center"
                  sx={
                    isDesignTable
                      ? DESIGN_ORDER_ROW_ACTIONS_CELL_SX
                      : QUOTATION_ROW_DELETE_CELL_SX
                  }
                >
                  <GroupCellIcon>
                    <Box
                      sx={
                        isDesignTable
                          ? DESIGN_ORDER_ROW_ACTIONS_BOX_SX
                          : QUOTATION_ROW_ACTIONS_BOX_SX
                      }
                    >
                      <Box
                        sx={
                          isDesignTable
                            ? DESIGN_ORDER_ROW_ACTIONS_LEADING_SLOT_SX
                            : QUOTATION_ROW_ACTIONS_LEADING_SLOT_SX
                        }
                      />
                      <GroupDeleteIconButton
                        isDesignTable={isDesignTable}
                        onClick={() => {
                          if (typeof handleDeleteGroupInstall === 'function') {
                            handleDeleteGroupInstall(group.id)
                            return
                          }
                          setSelectedProducts((prev) =>
                            prev.map((p) => {
                              if (String(p.group_id) !== String(group.id)) return p
                              const cleared = clearCatalogInstallCost(p)
                              return {
                                ...cleared,
                                retail_price: cleared.actual_retail_price,
                              }
                            }),
                          )
                          setRefreshCalculation((v) => !v)
                        }}
                      />
                    </Box>
                  </GroupCellIcon>
                </TableCell>
              )
            }
            return <TableCell key={`gi-x-${val.id}`} />
          })}
        </TableRow>
      ) : null}
      <TableRow>
        <TableCell colSpan={100} sx={{ p: 0 }}>
          <Collapse in={isGroupOpen} timeout="auto" unmountOnExit>
            <Box sx={{ px: 2, py: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    {VALUE?.map((val) => {
                      if (!shouldRenderValueColumn(val, isDesignTable, markupType)) return null
                      return val.id === 4 ? (
                        <Tooltip key={val.id} title="Product Formula Cost Per Unit * Quantity">
                          <TableCell>{val.value}</TableCell>
                        </Tooltip>
                      ) : val.id === 14 ? (
                        <Tooltip key={val.id} title="Selling Price With Marketing Adjustment">
                          <TableCell>{val.value}</TableCell>
                        </Tooltip>
                      ) : (
                        <TableCell key={`gh-${val.id}`}>{val.value}</TableCell>
                      )
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(group?.products || []).map((data, groupProductIndex) => {
                    const quantityDraft = groupProductQuantityDrafts[String(data.indexId)] || {}
                    const installQty =
                      Number(quantityDraft.group_install_quantity ?? data?.group_install_quantity) || 1
                    const productLocQty =
                      Number(quantityDraft.group_product_quantity ?? data?.group_product_quantity) || 1
                    const installRetailEach = Number(data?.install_cost) || 0
                    const retailEach =
                      effectiveMarkupType == 1
                        ? Number(data?.price || data?.product_price || 0)
                        : Number(
                            data?.actual_retail_price || data?.retail_price || data?.product_retail_price || 0,
                          )
                    const productSubTotal = retailEach * productLocQty
                    const installSubTotal = installRetailEach * installQty
                    const discountAmount = Number(data?.discount || 0)
                    const cellProductTotal = Math.max(
                      0,
                      productSubTotal + installSubTotal - discountAmount,
                    )
                    const { isProductLosingMoney, isInstallLosingMoney } =
                      getNestedLineLosingMoneyFlags(data, markupType)
                    const nestedProductRowSx = resolveLosingMoneyRowSx(isProductLosingMoney)
                    const nestedInstallRowSx = resolveLosingMoneyRowSx(isInstallLosingMoney)
                    const nestedAttachmentRowProps =
                      isDesignTable &&
                      typeof getProductAttachmentRowProps === 'function'
                        ? getProductAttachmentRowProps(data.indexId)
                        : null

                    return (
                      <Fragment key={`gp-${data.id || data.indexId}`}>
                        <TableRow
                          sx={{
                            ...getGroupRowAlignSx(),
                            ...nestedProductRowSx,
                            ...(nestedAttachmentRowProps?.sx || {}),
                          }}
                          {...(nestedAttachmentRowProps?.eventHandlers || {})}
                        >
                          <TableCell padding="checkbox" sx={getGroupIconCellSx()}>
                            {groupEditMode?.[group.id] && isQuotationChooseCatalogRow(data) ? (
                              <Checkbox
                                checked={isIndexIdInList(groupSelectedProductIds, data.indexId)}
                                onChange={() => toggleGroupProductSelection(data)}
                              />
                            ) : null}
                          </TableCell>
                          {VALUE?.map((val) => {
                            if (!shouldRenderValueColumn(val, isDesignTable, markupType)) return null
                            if (val.id === 1)
                              return <TableCell key={`gc1-${val.id}`}>{groupProductIndex + 1}</TableCell>
                            if (val.id === 24) {
                              return <TableCell key={`gc24-${val.id}`}>-</TableCell>
                            }
                            if (val.id === 22 && !isDesignTable) {
                              return (
                                <TableCell key={`gc22-${val.id}`}>
                                  <QuotationQuickbooksCell product={data} />
                                </TableCell>
                              )
                            }
                            if (val.id === 2)
                              return (
                                <TableCell key={`gc2-${val.id}`}>
                                  <Box>
                                    <Typography sx={{ wordBreak: 'break-word' }}>
                                      {data.product_name || data.name}
                                    </Typography>
                                    {data?.product_notes ? (
                                      <Typography
                                        sx={{
                                          color: theme.palette.error.main,
                                          wordBreak: 'break-word',
                                          fontSize: '0.8rem',
                                        }}
                                      >
                                        Note: {data.product_notes}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                </TableCell>
                              )
                            if (val.id === 25)
                              return (
                                <TableCell key={`gc25-${val.id}`}>
                                  {data.product_desc || data.description || ''}
                                </TableCell>
                              )
                            if (val.id === 3)
                              return (
                                <TableCell
                                  key={`gc3-${val.id}`}
                                  align={isDesignTable ? 'center' : undefined}
                                  sx={getGroupIconCellSx()}
                                >
                                  <GroupCellIcon>
                                    <IconButton disabled size="small">
                                      <NoteAdd
                                        sx={{
                                          color:
                                            !data?.product_notes || data?.product_notes === ''
                                              ? ''
                                              : theme.palette.primary.main,
                                        }}
                                      />
                                    </IconButton>
                                  </GroupCellIcon>
                                  {/* <IconButton onClick={() => handleOpenNote(data.indexId, data.product_notes)}>
                                    <NoteAdd
                                      sx={{
                                        color:
                                          !data?.product_notes || data?.product_notes === ''
                                            ? ''
                                            : theme.palette.primary.main,
                                      }}
                                    />
                                  </IconButton> */}
                                </TableCell>
                              )
                            if (val.id === 19)
                              return (
                                <TableCell
                                  key={`gc19-${val.id}`}
                                  align={isDesignTable ? 'center' : undefined}
                                  sx={getGroupIconCellSx()}
                                >
                                  <GroupCellIcon>
                                    <Tooltip title="Use group-level attachments for grouped products">
                                      <span>
                                        <IconButton disabled size="small">
                                          <UploadFile
                                            sx={{
                                              color: groupHasAttachments
                                                ? theme.palette.primary.main
                                                : 'error.main',
                                            }}
                                          />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </GroupCellIcon>
                                </TableCell>
                              )
                            if (val.id === 7 && !isDesignTable) {
                              return (
                                <TableCell key={`gc7-${val.id}`}>
                                  {isQuotationWriteRow(data) ? (
                                    '-'
                                  ) : data.recipe_id ? (
                                    <IconButton disabled size="small">
                                      <span className="heroicons-solid--beaker" />
                                    </IconButton>
                                  ) : null}
                                  {/* {isQuotationWriteRow(data) ? (
                                    '-'
                                  ) : (
                                    <Tooltip title="Product Formula">
                                      <IconButton onClick={() => handleOpenProductFormula(data.recipe_id)}>
                                        <span className="heroicons-solid--beaker" />
                                      </IconButton>
                                    </Tooltip>
                                  )} */}
                                </TableCell>
                              )
                            }
                            if (val.id === 4) {
                              const formulaCost = getSingleProductFormulaCostEach(data)
                              return (
                                <TableCell key={`gc4-${val.id}`}>
                                  {isDesignTable
                                    ? `$${fUSPrice(formulaCost.toFixed(2))}`
                                    : formulaCost.toFixed(2)}
                                </TableCell>
                              )
                            }
                            if (val.id === 20)
                              return (
                                <TableCell key={`gc20-${val.id}`}>
                                  {data.inhouse === 0
                                    ? data?.sub_contract_markup != null && data?.sub_contract_markup !== ''
                                      ? `${Number(data.sub_contract_markup).toFixed(2)}`
                                      : '-'
                                    : '-'}
                                </TableCell>
                              )
                            if (val.id === 5)
                              return (
                                <TableCell key={`gc5-${val.id}`} align={isDesignTable ? 'center' : undefined}>
                                  {isDesignTable
                                    ? `$${fUSPrice(retailEach.toFixed(2))}`
                                    : effectiveMarkupType == 2
                                      ? retailEach.toFixed(2)
                                      : null}
                                </TableCell>
                              )
                            if (val.id === 6)
                              return (
                                <TableCell key={`gc6-${val.id}`} align={isDesignTable ? 'center' : undefined}>
                                  {!isDesignTable && effectiveMarkupType == 1
                                    ? retailEach.toFixed(2)
                                    : null}
                                </TableCell>
                              )
                            if (val.id === 9)
                              return (
                                <TableCell key={`gc9-${val.id}`} align="center">
                                  {Number(productLocQty)}
                                  {/* {groupEditMode?.[group.id] ? (
                                    <TextField
                                      type="number"
                                      size="small"
                                      value={productLocQty}
                                      label="Qty"
                                      onChange={(event) =>
                                        handleGroupLineQuantityDraftChange(
                                          data.indexId,
                                          'group_product_quantity',
                                          event.target.value,
                                        )
                                      }
                                      sx={{ width: 80 }}
                                    />
                                  ) : (
                                    Number(productLocQty)
                                  )} */}
                                </TableCell>
                              )
                            if (val.id === 10)
                              return (
                                <TableCell key={`gc10-${val.id}`} align="center">
                                  <LocationOn
                                    sx={{
                                      color: data?.location?.length ? theme.palette.primary.main : '',
                                      opacity: 0.5,
                                    }}
                                  />
                                </TableCell>
                              )
                            if (val.id === 11)
                              return (
                                <TableCell key={`gc11-${val.id}`}>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ color: 'text.secondary', opacity: 0.7 }}
                                  >
                                    <Remove fontSize="small" />
                                    <Switch
                                      checked={getNestedLineMarketingAdjValueType(
                                        data,
                                        markupType,
                                        'product',
                                      )}
                                      disabled
                                    />
                                    <Add fontSize="small" />
                                  </Stack>
                                </TableCell>
                              )
                            if (val.id === 15)
                              return (
                                <TableCell key={`gc15-${val.id}`} align="center">
                                  {formatNestedMarketingAdjustmentDisplay(
                                    getNestedLineMarketingAdjustmentValue(data, markupType, 'product'),
                                  )}
                                </TableCell>
                              )
                            if (val.id === 12)
                              return (
                                <TableCell key={`gc12-${val.id}`}>
                                  ${fUSPrice(productSubTotal.toFixed(2))}
                                </TableCell>
                              )
                            if (val.id === 14) {
                              const hasInstall = Number(data?.install_cost || 0) > 0;
                              return (
                                <TableCell key={`gc14-${val.id}`}>
                                  {hasInstall ? '' : `$${fUSPrice(cellProductTotal.toFixed(2))}`}
                                </TableCell>
                              )
                            }
                            if (val.id === 13) return <TableCell key={`gc13-${val.id}`} />
                            return <TableCell key={`gcx-${val.id}`} />
                          })}
                        </TableRow>
                        {Number(data?.install_cost || 0) > 0 ? (
                          <TableRow sx={{ ...getGroupRowAlignSx(), ...nestedInstallRowSx }}>
                            <TableCell padding="checkbox" sx={getGroupIconCellSx()} />
                            {VALUE?.map((val) => {
                              if (!shouldRenderValueColumn(val, isDesignTable, markupType)) return null
                              if (val.id === 1) return <TableCell key={`gi1-${val.id}`} />
                              if (val.id === 24) return <TableCell key={`gi24-${val.id}`} />
                              if (val.id === 22 && !isDesignTable) return <TableCell key={`gi22-${val.id}`} />
                              if (val.id === 2)
                                return (
                                  <TableCell
                                    key={`gi2-${val.id}`}
                                    align={isDesignTable ? 'center' : undefined}
                                    sx={isDesignTable ? productColSx : undefined}
                                  >
                                    <Typography
                                      sx={
                                        // isDesignTable
                                        //   ? {textAlign: 'left',whiteSpace: 'pre-wrap',wordBreak: 'break-word'}
                                        //   :
                                           groupHasInstallNotes
                                            ? {
                                                color: 'red',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                              }
                                            : undefined
                                      }
                                    >
                                      {/* Design order keeps "Install Price"; note is shown via Notes icon only */}
                                      {
                                      // isDesignTable ||
                                       !groupHasInstallNotes
                                        ? 'Install Price'
                                        : groupInstallNotes}
                                    </Typography>
                                  </TableCell>
                                )
                              if (val.id === 25 || val.id === 19 || (val.id === 7 && !isDesignTable))
                                return <TableCell key={`gi25-${val.id}`} />
                              if (val.id === 3) {
                                return (
                                  <TableCell
                                    key={`gi3-${val.id}`}
                                    align="center"
                                    sx={getGroupIconCellSx()}
                                  >
                                    <GroupCellIcon>
                                      <IconButton disabled size="small">
                                        <NoteAdd
                                          sx={{
                                            color: groupHasInstallNotes
                                              ? theme.palette.primary.main
                                              : '',
                                          }}
                                        />
                                      </IconButton>
                                    </GroupCellIcon>
                                    {/* <IconButton
                                      onClick={() =>
                                        handleOpenInstallNote(data.indexId, data.install_notes)
                                      }
                                    >
                                      <NoteAdd
                                        sx={{
                                          color:
                                            !data?.install_notes || data?.install_notes === ''
                                              ? ''
                                              : theme.palette.primary.main,
                                        }}
                                      />
                                    </IconButton> */}
                                  </TableCell>
                                )
                              }
                              if (val.id === 4)
                                return (
                                  <TableCell key={`gi4-${val.id}`}>
                                    {data?.expected_install_labor_cost_with_markup?.toFixed(2) || 0}
                                  </TableCell>
                                )
                              if (val.id === 20) return <TableCell key={`gi20-${val.id}`} />
                              if (val.id === 5)
                                return (
                                  <TableCell key={`gi5-${val.id}`} align={isDesignTable ? 'center' : undefined}>
                                    {isDesignTable
                                      ? `$${fUSPrice(installRetailEach.toFixed(2))}`
                                      : effectiveMarkupType == 2
                                        ? installRetailEach.toFixed(2)
                                        : null}
                                  </TableCell>
                                )
                              if (val.id === 6)
                                return (
                                  <TableCell key={`gi6-${val.id}`} align={isDesignTable ? 'center' : undefined}>
                                    {!isDesignTable && effectiveMarkupType == 1
                                      ? installRetailEach.toFixed(2)
                                      : null}
                                  </TableCell>
                                )
                              if (val.id === 9)
                                return (
                                  <TableCell key={`gi9-${val.id}`} align="center">
                                    {installQty}
                                    {/* {groupEditMode?.[group.id] ? (
                                      <TextField
                                        type="number"
                                        size="small"
                                        value={installQty}
                                        label="Qty"
                                        onChange={(event) =>
                                          handleGroupLineQuantityDraftChange(
                                            data.indexId,
                                            'group_install_quantity',
                                            event.target.value,
                                          )
                                        }
                                        sx={{ width: 80 }}
                                      />
                                    ) : (
                                      installQty
                                    )} */}
                                  </TableCell>
                                )
                              if (val.id === 10) return <TableCell key={`gi10-${val.id}`}>-</TableCell>
                              if (val.id === 11) {
                                return (
                                  <TableCell key={`gi11-${val.id}`}>
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      sx={{ color: 'text.secondary', opacity: 0.7 }}
                                    >
                                      <Remove fontSize="small" />
                                      <Switch
                                        checked={getNestedLineMarketingAdjValueType(
                                          data,
                                          markupType,
                                          'install',
                                        )}
                                        disabled
                                      />
                                      <Add fontSize="small" />
                                    </Stack>
                                  </TableCell>
                                )
                              }
                              if (val.id === 15) {
                                return (
                                  <TableCell key={`gi15-${val.id}`} align="center">
                                    {formatNestedMarketingAdjustmentDisplay(
                                      getNestedLineMarketingAdjustmentValue(data, markupType, 'install'),
                                    )}
                                  </TableCell>
                                )
                              }
                              if (val.id === 12)
                                return (
                                  <TableCell key={`gi12-${val.id}`}>
                                    {(installRetailEach * installQty).toFixed(2)}
                                  </TableCell>
                                )
                              if (val.id === 14)
                                return (
                                  <TableCell key={`gi14-${val.id}`}>
                                    ${fUSPrice(cellProductTotal.toFixed(2))}
                                  </TableCell>
                                )
                              if (val.id === 13) return <TableCell key={`gi13-${val.id}`} />
                              return <TableCell key={`gix-${val.id}`} />
                            })}
                          </TableRow>
                        ) : null}
                      </Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      {(group?.products || []).map((gp) => (
        <Fragment key={`group-product-att-modal-${group.id}-${gp.indexId}`}>
          {typeof renderProductAttachmentModal === 'function'
            ? renderProductAttachmentModal(gp.indexId)
            : null}
        </Fragment>
      ))}
      <Modal
        open={groupLocationStates[group.id]}
        handleClose={() => {
          setGroupLocationStates((prev) => ({ ...prev, [group.id]: false }))
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={group.groupAllLocationSame}
                onChange={() => handleGroupAllLocationSameChange(group.id)}
              />
            }
            label="All Locations Same"
          />
          {projectCategoryId !== 14 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '50%', alignItems: 'center', mt: 2 }}>
              <LabelStyle>Qty Location Settings</LabelStyle>
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <Typography variant="subtitle2">Multiple</Typography>
                <Switch
                  color="primary"
                  checked={group.groupIsSingleCard}
                  onChange={(event) => handleGroupSingleCardChange(group.id, event.target.checked)}
                />
                <Typography variant="subtitle2">Single</Typography>
              </Box>
            </Box>
          ) : null}
        </Stack>
        <Grid container spacing={3} sx={{ justifyContent: 'center', width: '100%' }}>
          {(group.groupAddresses || []).map((addr, i) => (
            <Grid item xs={12} key={`group-location-${group.id}-${i}`}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Location {i + 1}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Address"
                  size="small"
                  value={addr?.address || ''}
                  disabled={i !== 0 && group.groupAllLocationSame}
                  onChange={(event) =>
                    handleGroupLocationFieldChange(group.id, i, 'address', event.target.value)
                  }
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="City"
                  size="small"
                  value={addr?.city || ''}
                  disabled={i !== 0 && group.groupAllLocationSame}
                  onChange={(event) =>
                    handleGroupLocationFieldChange(group.id, i, 'city', event.target.value)
                  }
                />
                <TextField
                  fullWidth
                  label="State"
                  size="small"
                  value={addr?.state || ''}
                  disabled={i !== 0 && group.groupAllLocationSame}
                  onChange={(event) =>
                    handleGroupLocationFieldChange(group.id, i, 'state', event.target.value)
                  }
                />
                <TextField
                  fullWidth
                  label="Zip"
                  size="small"
                  value={addr?.zip || ''}
                  disabled={i !== 0 && group.groupAllLocationSame}
                  onChange={(event) =>
                    handleGroupLocationFieldChange(group.id, i, 'zip', event.target.value)
                  }
                />
              </Stack>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <LoadingButton
                variant="contained"
                onClick={() => {
                  setGroupLocationStates((prev) => ({ ...prev, [group.id]: false }))
                }}
              >
                Save
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>
      </Modal>
    </>
  
  )
}

export function QuotationGroupSummaryRow({
  group,
  index,
  isGroupOpen,
  provided,
  getGroupAnchorIndexId,
  selectedProducts,
  getGroupLosingMoneyFlags,
  getLosingMoneyRowSx,
  setOpenGroupedRows,
  groupEditMode,
  handleGroupFieldChange,
  handleOpenGroupNote,
  handleOpenAttachment,
  getProductAttachmentsList,
  theme,
  handleGroupQuantityChange,
  setGroupLocationStates,
  handleGroupMarketingTypeChange,
  handleGroupMarketingAdjustmentChange,
  hasMarkupPermission,
  markupType,
  hasNegativeMarketingPermission,
  handleDeleteGroup,
  setGroupEditMode,
  groupSelectedProductIds,
  setGroupSelectedProductIds,
  enqueueSnackbar,
  getSelectedLineCatalogCount,
  setSelectedProducts,
  groupProductQuantityDrafts,
  clearGroupLineQuantityDrafts,
  resetGroupMarketingAdjustments,
  openGroupProductNoteIndex,
  setOpenGroupProductNoteIndex,
  openGroupInstallNoteIndex,
  setOpenGroupInstallNoteIndex,
  currentNote,
  setCurrentNote,
  handleNoteChange,
  handleSaveNote,
  handleClearNote,
  handleSaveInstallNote,
  handleClearInstallNote,
  isSavingNote,
  isClearingNote,
  MAX_CHARACTERS,
  expandedTableProps,
  tableVariant = 'quotation',
}) {
  const isDesignTable = tableVariant === 'design'
  const productColSx = expandedTableProps?.productColSx
  const descriptionColSx = expandedTableProps?.descriptionColSx
  const groupAttachmentIndexId = getGroupAnchorIndexId(group)
  const groupAttachmentRowProps =
    isDesignTable &&
    groupAttachmentIndexId != null &&
    typeof expandedTableProps?.getProductAttachmentRowProps === 'function'
      ? expandedTableProps.getProductAttachmentRowProps(groupAttachmentIndexId)
      : null
  const groupMemberProducts = (selectedProducts || []).filter(
    (p) => String(p?.group_id) === String(group.id),
  )
  const groupDisplayNote =
    groupMemberProducts.find(
      (p) =>
        p?.product_notes != null &&
        String(p.product_notes).trim() !== '',
    )?.product_notes || ''
  const {
    isGroupProductLosingMoney,
    isGroupInstallLosingMoney,
  } = getGroupLosingMoneyFlags(group)
  const groupProductLosingMoneyRowSx = getLosingMoneyRowSx(
    isGroupProductLosingMoney,
  )
  const groupInstallLosingMoneyRowSx = getLosingMoneyRowSx(
    isGroupInstallLosingMoney,
  )
  const displaySlNo = index + 1

  const handleGroupEditClick = () => {
    setGroupEditMode({ [group.id]: true })
    setGroupSelectedProductIds(
      (group?.products || [])
        .filter((p) => isQuotationChooseCatalogRow(p))
        .map((p) => p.indexId),
    )
    clearGroupLineQuantityDrafts(group?.products)
  }

  const handleGroupCancelEditClick = () => {
    setGroupEditMode({})
    setGroupSelectedProductIds([])
    clearGroupLineQuantityDrafts(group?.products)
  }

  const handleGroupUpdateClick = () => {
    const productIdsInGroup = (group?.products || []).map((p) => p.indexId)
    const remainingIds = productIdsInGroup.filter((id) =>
      isIndexIdInList(groupSelectedProductIds, id),
    )
    if (remainingIds.length < 2) {
      enqueueSnackbar('A group must contain at least 2 products.', { variant: 'error' })
      return
    }
    // Client request: remove restriction that blocks grouping when there are no catalog ("choose") rows.
    // if (getSelectedLineCatalogCount(remainingIds) === 0) {
    //   enqueueSnackbar(
    //     'A group must contain at least one catalog line item.',
    //     { variant: 'error' },
    //   )
    //   return
    // }
    const idsToRemove = productIdsInGroup.filter(
      (id) => !isIndexIdInList(groupSelectedProductIds, id),
    )
    const idsToAdd = groupSelectedProductIds.filter(
      (id) => !isIndexIdInList(productIdsInGroup, id),
    )
    if (idsToRemove.length > 0 || idsToAdd.length > 0) {
      resetGroupMarketingAdjustments(group.id)
      setSelectedProducts((prev) => {
        const existingAnchor = (prev || []).find(
          (x) =>
            String(x.group_id) === String(group.id) &&
            x.group_catalog_anchor_index_id != null,
        )?.group_catalog_anchor_index_id
        return (prev || []).map((p) => {
          if (isIndexIdInList(idsToRemove, p.indexId)) {
            return restoreUngroupedProductMarketing(p, markupType, {
              hasNegativePermission: hasNegativeMarketingPermission,
            })
          }
          if (isIndexIdInList(idsToAdd, p.indexId)) {
            const existingMembers = (prev || []).filter(
              (x) =>
                String(x.group_id) === String(group.id) &&
                !isIndexIdInList(idsToAdd, x.indexId),
            )
            return withInheritedGroupNotes(
              {
                ...p,
                ...snapshotGroupMemberMarketing(p),
                group_id: group.id,
                group_name: group.name || 'Group',
                group_description: group.description || '',
                group_catalog_anchor_index_id:
                  existingAnchor ?? p.group_catalog_anchor_index_id,
                adjustment: 0,
                adj_value_type: true,
                install_adjustment: 0,
                install_adj_value_type: true,
              },
              existingMembers,
            )
          }
          if (String(p.group_id) === String(group.id)) {
            return {
              ...p,
              adjustment: 0,
              adj_value_type: true,
              install_adjustment: 0,
              install_adj_value_type: true,
            }
          }
          return p
        })
      })
    }
    const groupProductIds = new Set(
      (group?.products || []).map((p) => String(p.indexId)),
    )
    const hasLineQuantityDrafts = Object.keys(groupProductQuantityDrafts || {}).some(
      (productIndexId) => groupProductIds.has(String(productIndexId)),
    )
    if (hasLineQuantityDrafts) {
      setSelectedProducts((prev) =>
        (prev || []).map((product) => {
          if (!groupProductIds.has(String(product.indexId))) {
            return product
          }
          const draft = groupProductQuantityDrafts[String(product.indexId)]
          if (!draft) return product
          return {
            ...product,
            group_product_quantity:
              draft.group_product_quantity ?? product.group_product_quantity ?? 1,
            group_install_quantity:
              draft.group_install_quantity ?? product.group_install_quantity ?? 1,
          }
        }),
      )
      clearGroupLineQuantityDrafts(group?.products)
    }
    setGroupEditMode({})
    setGroupSelectedProductIds([])
  }

  const handleGroupEditUpdateClick = () => {
    if (groupEditMode?.[group.id]) {
      handleGroupUpdateClick()
    } else {
      handleGroupEditClick()
    }
  }

  return (
    <Draggable
      key={`group-${group.id}`}
      draggableId={`group-${group.id}`}
      index={index}
    >
      {(provided) => (
                                            <Fragment>
                                              <TableRow
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...(groupAttachmentRowProps?.eventHandlers || {})}
                                                sx={{
                                                  ...(groupProductLosingMoneyRowSx ||
                                                    groupInstallLosingMoneyRowSx),
                                                  ...getGroupRowAlignSx(),
                                                  ...(groupAttachmentRowProps?.sx || {}),
                                                }}
                                              >
                                                <TableCell
                                                  padding="checkbox"
                                                  {...provided.dragHandleProps}
                                                  sx={{
                                                    width: 40,
                                                    cursor: 'grab',
                                                    ...(getGroupIconCellSx() || {}),
                                                  }}
                                                >
                                                  <DragIndicator fontSize="small" color="action" />
                                                </TableCell>
                                                <TableCell padding="checkbox" sx={getGroupIconCellSx()}>
                                                  <GroupCellIcon>
                                                    <IconButton
                                                      size="small"
                                                      color="primary"
                                                      onClick={() =>
                                                        setOpenGroupedRows((prev) => ({
                                                          ...prev,
                                                          [group.id]: !(prev?.[group.id] === true),
                                                        }))
                                                      }
                                                    >
                                                      {isGroupOpen ? <Remove /> : <Add />}
                                                    </IconButton>
                                                  </GroupCellIcon>
                                                </TableCell>
                                                <TableCell
                                                  align={isDesignTable ? 'center' : undefined}
                                                  sx={!isDesignTable ? { fontWeight: 700 } : undefined}
                                                >
                                                  #{displaySlNo}
                                                </TableCell>
                                                <TableCell align={isDesignTable ? 'center' : undefined}>-</TableCell>
                                                {!isDesignTable ? (
                                                <TableCell>
                                                  <QuotationQuickbooksCell
                                                    products={
                                                      group?.products?.length
                                                        ? group.products
                                                        : groupMemberProducts
                                                    }
                                                  />
                                                </TableCell>
                                                ) : null}
                                                <TableCell
                                                  align={isDesignTable ? 'center' : undefined}
                                                  sx={
                                                    isDesignTable
                                                      ? productColSx
                                                      : { fontWeight: 700, minWidth: 140 }
                                                  }
                                                >
                                                  <Box>
                                                    {groupEditMode?.[group.id] ? (
                                                      <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        value={group.name || ''}
                                                        onChange={(e) =>
                                                          handleGroupFieldChange(group.id, 'name', e.target.value)
                                                        }
                                                      />
                                                    ) : (
                                                      <Typography
                                                        sx={{
                                                          fontWeight: isDesignTable ? 600 : 700,
                                                          textAlign: isDesignTable ? 'center' : undefined,
                                                        }}
                                                      >
                                                        {group.name || 'Group'}
                                                      </Typography>
                                                    )}
                                                    {groupDisplayNote ? (
                                                      <Typography sx={{ color: 'red', textAlign: isDesignTable ? 'center' : undefined }}>
                                                        Note: {groupDisplayNote}
                                                      </Typography>
                                                    ) : null}
                                                  </Box>
                                                </TableCell>
                                                <TableCell
                                                  align={isDesignTable ? 'center' : undefined}
                                                  sx={
                                                    isDesignTable
                                                      ? descriptionColSx
                                                      : { minWidth: 160 }
                                                  }
                                                >
                                                  {groupEditMode?.[group.id] ? (
                                                    <TextField
                                                      variant="outlined"
                                                      size="small"
                                                      fullWidth
                                                      multiline
                                                      minRows={2}
                                                      maxRows={3}
                                                      value={group.description || ''}
                                                      placeholder="Description"
                                                      onChange={(e) =>
                                                        handleGroupFieldChange(
                                                          group.id,
                                                          'description',
                                                          e.target.value,
                                                        )
                                                      }
                                                    />
                                                  ) : (
                                                    <Typography
                                                      sx={{
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        color: 'inherit',
                                                        textAlign: isDesignTable ? 'center' : undefined,
                                                      }}
                                                    >
                                                      {group.description || (isDesignTable ? '-' : 'No description')}
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                                <TableCell
                                                  align={isDesignTable ? 'center' : undefined}
                                                  sx={getGroupIconCellSx()}
                                                >
                                                  <GroupCellIcon>
                                                    {groupAttachmentIndexId != null ? (
                                                      <IconButton
                                                        size={isDesignTable ? 'small' : 'medium'}
                                                        onClick={() =>
                                                          handleOpenGroupNote(
                                                            groupAttachmentIndexId,
                                                            groupDisplayNote || '',
                                                          )
                                                        }
                                                      >
                                                        <NoteAdd
                                                          sx={{
                                                            color: !groupDisplayNote
                                                              ? ''
                                                              : theme.palette.primary.main,
                                                          }}
                                                        />
                                                      </IconButton>
                                                    ) : null}
                                                  </GroupCellIcon>
                                                </TableCell>
                                                <TableCell
                                                  align={isDesignTable ? 'center' : undefined}
                                                  sx={getGroupIconCellSx()}
                                                >
                                                  <GroupCellIcon>
                                                    {groupAttachmentIndexId != null ? (
                                                      <Tooltip title="Drop files on row or click to open attachments">
                                                        <IconButton
                                                          size={isDesignTable ? 'small' : 'medium'}
                                                          onClick={() =>
                                                            handleOpenAttachment(groupAttachmentIndexId)
                                                          }
                                                        >
                                                          {expandedTableProps?.isUploadingProduct?.[
                                                            groupAttachmentIndexId
                                                          ] ||
                                                          expandedTableProps?.isUploadingProduct?.[
                                                            String(groupAttachmentIndexId)
                                                          ] ? (
                                                            <CircularProgress size={34} />
                                                          ) : (
                                                            <UploadFile
                                                              sx={{
                                                                color:
                                                                  getProductAttachmentsList(
                                                                    groupAttachmentIndexId,
                                                                  ).length === 0
                                                                    ? 'error.main'
                                                                    : theme.palette.primary.main,
                                                              }}
                                                            />
                                                          )}
                                                        </IconButton>
                                                      </Tooltip>
                                                    ) : null}
                                                  </GroupCellIcon>
                                                </TableCell>
                                                {!isDesignTable ? <TableCell>-</TableCell> : null}
                                                <TableCell
                                                  align={isDesignTable ? 'center' : undefined}
                                                  sx={!isDesignTable ? { fontWeight: 600 } : undefined}
                                                >
                                                  {isDesignTable
                                                    ? `$${fUSPrice(Number(group.baseProductFormulaEach || 0))}`
                                                    : Number(group.baseProductFormulaEach || 0).toFixed(2)}
                                                </TableCell>
                                                <TableCell
                                                  align="center"
                                                  sx={!isDesignTable ? { fontWeight: 600 } : undefined}
                                                >
                                                  -
                                                </TableCell>
                                                {isDesignTable ? (
                                                  <TableCell align="center">
                                                    {hasMarkupPermission
                                                      ? `$${fUSPrice(Number(group.adjustedGroupRetailEach || 0).toFixed(2))}`
                                                      : '-'}
                                                    {/* Future: read-only TextField for design group retail
                                                    {hasMarkupPermission ? (
                                                      <TextField
                                                        size="small"
                                                        label="Retail Price Each"
                                                        sx={{ width: 120 }}
                                                        value={Number(group.adjustedGroupRetailEach || 0).toFixed(2)}
                                                        InputProps={{ readOnly: true }}
                                                      />
                                                    ) : null}
                                                    */}
                                                  </TableCell>
                                                ) : (
                                                  <>
                                                    <TableCell sx={{ fontWeight: 600 }}>
                                                      {hasMarkupPermission && markupType == 2
                                                        ? Number(group.adjustedGroupRetailEach || 0).toFixed(2)
                                                        : null}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>
                                                      {hasMarkupPermission && markupType == 1
                                                        ? Number(group.adjustedGroupRetailEach || 0).toFixed(2)
                                                        : null}
                                                    </TableCell>
                                                  </>
                                                )}
                                                <TableCell align={isDesignTable ? 'center' : undefined} sx={!isDesignTable ? { width: 110 } : undefined}>
                                                  <TextField
                                                    type="number"
                                                    {...(isDesignTable
                                                      ? { size: 'small', style: { width: 72 } }
                                                      : { fullWidth: true, label: 'Qty' })}
                                                    value={Number(group.groupQuantity || 1)}
                                                    onChange={(event) =>
                                                      handleGroupQuantityChange(group.id, event.target.value)
                                                    }
                                                    InputProps={{ inputProps: { min: 0 } }}
                                                  />
                                                </TableCell>
                                                <TableCell align="center" sx={getGroupIconCellSx()}>
                                                  <GroupCellIcon>
                                                    <IconButton
                                                      color="primary"
                                                      size={isDesignTable ? 'small' : 'medium'}
                                                      onClick={() =>
                                                        setGroupLocationStates((prev) => ({
                                                          ...prev,
                                                          [group.id]: true,
                                                        }))
                                                      }
                                                    >
                                                      <LocationOn
                                                        sx={{
                                                          color: (group.groupAddresses || []).some((obj) =>
                                                            [obj.address, obj.city, obj.state, obj.zip].some(
                                                              (value) => value === '' || value === null,
                                                            ),
                                                          )
                                                            ? ''
                                                            : theme.palette.primary.main,
                                                        }}
                                                      />
                                                    </IconButton>
                                                  </GroupCellIcon>
                                                </TableCell>
                                                <TableCell align={isDesignTable ? 'center' : undefined} sx={!isDesignTable ? { width: 150 } : undefined}>
                                                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                                                    <Remove
                                                      fontSize="small"
                                                      style={{ cursor: 'pointer' }}
                                                      onClick={() =>
                                                        handleGroupMarketingTypeChange(
                                                          group.id,
                                                          false,
                                                          group.totalProductFormulaEach,
                                                          group.totalProductRetailEach,
                                                          group.groupAdjustment,
                                                        )
                                                      }
                                                    />
                                                    <FormControlLabel
                                                      control={
                                                        <Switch
                                                          checked={
                                                            group.groupAdjValueType !== false &&
                                                            group.groupAdjValueType !== 0
                                                          }
                                                          onChange={(event) =>
                                                            handleGroupMarketingTypeChange(
                                                              group.id,
                                                              event.target.checked,
                                                              group.totalProductFormulaEach,
                                                              group.totalProductRetailEach,
                                                              group.groupAdjustment,
                                                            )
                                                          }
                                                        />
                                                      }
                                                      label=""
                                                    />
                                                    <Add
                                                      fontSize="small"
                                                      style={{ cursor: 'pointer' }}
                                                      onClick={() =>
                                                        handleGroupMarketingTypeChange(
                                                          group.id,
                                                          true,
                                                          group.totalProductFormulaEach,
                                                          group.totalProductRetailEach,
                                                          group.groupAdjustment,
                                                        )
                                                      }
                                                    />
                                                  </Stack>
                                                </TableCell>
                                                <TableCell align={isDesignTable ? 'center' : undefined} sx={!isDesignTable ? { fontWeight: 600, width: 120 } : undefined}>
                                                  <TextField
                                                    type="number"
                                                    {...(isDesignTable ? { size: 'small', style: { width: 90 } } : { fullWidth: true })}
                                                    inputProps={{ min: 0 }}
                                                    label="$"
                                                    value={Number(group.groupAdjustment) || ''}
                                                    onChange={(event) =>
                                                      handleGroupMarketingAdjustmentChange(
                                                        group.id,
                                                        event.target.value,
                                                        group.totalProductFormulaEach,
                                                        group.totalProductRetailEach,
                                                        group.groupAdjValueType,
                                                      )
                                                    }
                                                  />
                                                </TableCell>
                                                <TableCell align={isDesignTable ? 'center' : undefined} sx={!isDesignTable ? { fontWeight: 600 } : undefined}>
                                                  ${fUSPrice(Number(group.adjustedGroupSubtotal || 0).toFixed(2))}
                                                </TableCell>
                                                <TableCell align={isDesignTable ? 'center' : undefined} sx={!isDesignTable ? { fontWeight: 600 } : undefined}>
                                                  {Number(group.totalInstallSubtotal || 0) > 0
                                                    ? ''
                                                    : isDesignTable
                                                      ? `$${fUSPrice(Number(group.adjustedGroupProductTotal || 0).toFixed(2))}`
                                                      : Number(group.adjustedGroupProductTotal || 0).toFixed(2)}
                                                </TableCell>
                                                {isDesignTable ? (
                                                  <TableCell align="center" sx={DESIGN_ORDER_ROW_ACTIONS_CELL_SX}>
                                                    <GroupCellIcon>
                                                      <Box sx={DESIGN_ORDER_ROW_ACTIONS_BOX_SX}>
                                                        <Box sx={DESIGN_ORDER_ROW_ACTIONS_LEADING_SLOT_SX}>
                                                            <GroupEditActionButtons
                                                              isDesignTable={isDesignTable}
                                                              isEditing={!!groupEditMode?.[group.id]}
                                                              onEdit={handleGroupEditClick}
                                                              onUpdate={handleGroupUpdateClick}
                                                              onCancel={handleGroupCancelEditClick}
                                                            />
                                                        </Box>
                                                        <IconButton
                                                          color="error"
                                                          onClick={() => handleDeleteGroup(group.id)}
                                                        >
                                                          <Delete />
                                                        </IconButton>
                                                      </Box>
                                                    </GroupCellIcon>
                                                  </TableCell>
                                                ) : (
                                                  <TableCell
                                                    align="center"
                                                    sx={{ ...QUOTATION_ROW_DELETE_CELL_SX, fontWeight: 700 }}
                                                  >
                                                      <GroupCellIcon>
                                                        <Box sx={QUOTATION_ROW_ACTIONS_BOX_SX}>
                                                          <Box sx={QUOTATION_ROW_ACTIONS_LEADING_SLOT_SX}>
                                                            <GroupEditActionButtons
                                                              isDesignTable={isDesignTable}
                                                              isEditing={!!groupEditMode?.[group.id]}
                                                              onEdit={handleGroupEditClick}
                                                              onUpdate={handleGroupUpdateClick}
                                                              onCancel={handleGroupCancelEditClick}
                                                            />
                                                          </Box>
                                                          <GroupDeleteIconButton
                                                            isDesignTable={isDesignTable}
                                                            onClick={() => handleDeleteGroup(group.id)}
                                                          />
                                                        </Box>
                                                      </GroupCellIcon>
                                                  </TableCell>
                                                )}
                                              </TableRow>
                                              <QuotationGroupExpandedTable
                                                group={group}
                                                isGroupOpen={isGroupOpen}
                                                groupInstallLosingMoneyRowSx={groupInstallLosingMoneyRowSx || groupProductLosingMoneyRowSx}
                                                tableVariant={tableVariant}
                                                displaySlNo={displaySlNo}
                                                {...expandedTableProps}
                                              />
                                              {groupAttachmentIndexId != null &&
                                                typeof expandedTableProps?.renderProductAttachmentModal ===
                                                  'function' &&
                                                expandedTableProps.renderProductAttachmentModal(
                                                  groupAttachmentIndexId,
                                                )}
                                              {groupAttachmentIndexId != null && (
                                                <Modal
                                                  open={
                                                    openGroupProductNoteIndex === groupAttachmentIndexId
                                                  }
                                                  handleClose={() => {
                                                    setOpenGroupProductNoteIndex(null)
                                                    setCurrentNote?.('')
                                                  }}
                                                >
                                                  <Grid container spacing={3} sx={{ justifyContent: 'center', width: '100%' }}>
                                                    <Grid item xs={12}>
                                                      <Typography variant="h5" sx={{ mb: 2 }}>
                                                        Note
                                                      </Typography>
                                                      <TextField
                                                        fullWidth
                                                        multiline
                                                        rows={4}
                                                        value={currentNote}
                                                        onChange={handleNoteChange}
                                                        inputProps={{ maxLength: MAX_CHARACTERS }}
                                                      />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                                        <LoadingButton
                                                          variant="contained"
                                                          onClick={() => handleSaveNote(groupAttachmentIndexId)}
                                                          loading={isSavingNote}
                                                        >
                                                          Save
                                                        </LoadingButton>
                                                        <LoadingButton
                                                          variant="outlined"
                                                          color="error"
                                                          onClick={() => handleClearNote(groupAttachmentIndexId)}
                                                          loading={isClearingNote}
                                                        >
                                                          Clear
                                                        </LoadingButton>
                                                      </Box>
                                                    </Grid>
                                                  </Grid>
                                                </Modal>
                                              )}
      </Fragment>
      )}
    </Draggable>
  )
}
